/**
 * The signed-in session, and where it is kept.
 *
 * Tokens live in **sessionStorage**, not localStorage: a reload keeps the trader
 * signed in (the E2E test depends on it), closing the tab ends the session, and
 * a shared desk machine does not leave a bearer token behind for the next shift.
 * The *layout* is in localStorage, because losing the arrangement on an expiry is
 * exactly what the brief says must not happen.
 *
 * Access tokens live 15 minutes (docs/10 §3.1). `needsRefresh` says when to
 * exchange the refresh token silently; only a failed refresh sends the trader
 * back to the login screen.
 */

export interface Session {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Server-provided expiry, as a local epoch ms instant. */
  readonly expiresAtMs: number;
  readonly tenantId: string;
  readonly tenantSlug: string;
  readonly accountId: string;
  readonly sessionSeq: number;
  readonly entitlementsVersion: number;
  /**
   * Features the server says this session may use (P9). Rendering only: the
   * server refuses the same features independently, so this list is a hint,
   * never the control. Absent on a session stored before P9.
   */
  readonly features: readonly string[];
  /** The login the trader typed, for the header. Not a secret. */
  readonly login: string;
}

const STORAGE_KEY = 'weke.terminal.session.v2';

/** Refresh this long before expiry, so a command never races the deadline. */
const REFRESH_MARGIN_MS = 90_000;

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  tenant_id: string;
  tenant_slug: string;
  account_id: string;
  session_seq: number;
  entitlements_version: number;
  /** P9. Optional so a pre-P9 server response still parses. */
  features?: readonly string[];
  protocol_version: number;
  market_data_version: number;
}

export function sessionFromLogin(body: LoginResponse, login: string, nowMs: number): Session {
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAtMs: nowMs + body.expires_in * 1000,
    tenantId: body.tenant_id,
    tenantSlug: body.tenant_slug,
    accountId: body.account_id,
    sessionSeq: body.session_seq,
    entitlementsVersion: body.entitlements_version,
    features: body.features ?? [],
    login,
  };
}

export function needsRefresh(session: Session, nowMs: number): boolean {
  return session.expiresAtMs - REFRESH_MARGIN_MS <= nowMs;
}

export function isExpired(session: Session, nowMs: number): boolean {
  return session.expiresAtMs <= nowMs;
}

export function loadSession(): Session | null {
  const raw = safeGet(STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.tenantId !== 'string' ||
      typeof parsed.accountId !== 'string' ||
      typeof parsed.expiresAtMs !== 'number'
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAtMs: parsed.expiresAtMs,
      tenantId: parsed.tenantId,
      tenantSlug: typeof parsed.tenantSlug === 'string' ? parsed.tenantSlug : '',
      accountId: parsed.accountId,
      sessionSeq: typeof parsed.sessionSeq === 'number' ? parsed.sessionSeq : 0,
      entitlementsVersion:
        typeof parsed.entitlementsVersion === 'number' ? parsed.entitlementsVersion : 1,
      features: Array.isArray(parsed.features)
        ? parsed.features.filter((value): value is string => typeof value === 'string')
        : [],
      login: typeof parsed.login === 'string' ? parsed.login : '',
    };
  } catch {
    return null;
  }
}

export function saveSession(session: Session | null): void {
  try {
    if (session === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Private mode or a full quota: the session still works for this page.
  }
}

function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
